import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

// ── Cierre de sesión por inactividad ────────────────────────────────────────
// Se guarda en localStorage (no en memoria) para que la actividad en
// cualquier pestaña abierta del sistema mantenga viva la sesión en todas.
const IDLE_LIMIT_MS   = 30 * 60 * 1000; // 30 minutos
const CHECK_INTERVAL_MS = 15 * 1000;    // revisa cada 15s, suficiente para 30min
const ACTIVITY_KEY    = 'pivasa_last_activity';
const LOGOUT_REASON_KEY = 'pivasa_logout_reason';
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'wheel', 'touchstart'];
// Throttle de escritura a localStorage — no hace falta registrar cada pixel de mousemove
const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000;

async function fetchMenuKeys(roleId, roleIsAdmin) {
  // null = ver todo (sin restricción)
  if (!roleId || roleIsAdmin) return null;
  try {
    const r = await api.get(`/api/roles/${roleId}/items`);
    return new Set(r.data?.items || []);
  } catch {
    return null; // fallback: ver todo
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuKeys, setMenuKeys] = useState(null); // null = sin restricción
  const lastActivityWriteRef = useRef(0);

  useEffect(() => {
    const token = localStorage.getItem('pivasa_token');
    const stored = localStorage.getItem('pivasa_user');
    if (token && stored) {
      try {
        const parsed = JSON.parse(stored);
        setUser(parsed);
        fetchMenuKeys(parsed.role_id, parsed.role_is_admin).then(setMenuKeys);
      } catch {
        localStorage.removeItem('pivasa_token');
        localStorage.removeItem('pivasa_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (token, userData) => {
    localStorage.setItem('pivasa_token', token);
    localStorage.setItem('pivasa_user', JSON.stringify(userData));
    localStorage.setItem(ACTIVITY_KEY, String(Date.now()));
    setUser(userData);
    setMenuKeys(await fetchMenuKeys(userData.role_id, userData.role_is_admin));
  };

  const logout = (reason) => {
    localStorage.removeItem('pivasa_token');
    localStorage.removeItem('pivasa_user');
    localStorage.removeItem(ACTIVITY_KEY);
    if (reason) localStorage.setItem(LOGOUT_REASON_KEY, reason);
    setUser(null);
    setMenuKeys(null);
  };

  // ── Monitor de inactividad: cierra sesión tras 30 min sin actividad ────────
  useEffect(() => {
    if (!user) return undefined;

    const registerActivity = () => {
      const now = Date.now();
      if (now - lastActivityWriteRef.current < ACTIVITY_WRITE_THROTTLE_MS) return;
      lastActivityWriteRef.current = now;
      localStorage.setItem(ACTIVITY_KEY, String(now));
    };
    registerActivity();
    ACTIVITY_EVENTS.forEach(ev => window.addEventListener(ev, registerActivity, { passive: true }));

    const checkIdle = () => {
      const last = parseInt(localStorage.getItem(ACTIVITY_KEY), 10) || Date.now();
      if (Date.now() - last >= IDLE_LIMIT_MS) {
        logout('inactivity');
        window.location.href = '/login';
      }
    };

    // El navegador "congela" o limita mucho setInterval cuando la pestaña
    // queda en segundo plano (minimizada / otra pestaña activa) — que es
    // justo el escenario típico de "dejar el sistema inactivo 30 min". El
    // interval solo no basta: por eso también se revisa contra el timestamp
    // real guardado al volver a la pestaña (visibilitychange/focus), en vez
    // de depender de que el timer haya seguido corriendo mientras estaba oculta.
    const onVisibilityOrFocus = () => {
      if (document.visibilityState === 'hidden') return;
      checkIdle();
    };
    document.addEventListener('visibilitychange', onVisibilityOrFocus);
    window.addEventListener('focus', onVisibilityOrFocus);

    const interval = setInterval(checkIdle, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach(ev => window.removeEventListener(ev, registerActivity));
      document.removeEventListener('visibilitychange', onVisibilityOrFocus);
      window.removeEventListener('focus', onVisibilityOrFocus);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const refreshMenuKeys = async () => {
    if (!user) return;
    setMenuKeys(await fetchMenuKeys(user.role_id, user.role_is_admin));
  };

  const isSuperAdmin = user?.role === 'superadmin';

  return (
    <AuthContext.Provider value={{
      user, login, logout, isAuthenticated: !!user, loading, isSuperAdmin,
      menuKeys, refreshMenuKeys,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
